import { Button } from "@/components/ui/button";
import { PermissionButton } from "@/components/ui/permission-button";
import { EMPLOYEE_STATUSES, normalizeEmploymentValue } from "@shared/constants/employment";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { EMPLOYMENT_TYPES } from "@/lib/usStates";
import { ProfilePictureUpload } from "@/components/ui/profile-picture-upload";
import { toast } from "@/hooks/use-toast";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { AddressFields } from "@/components/forms/AddressFields";
import { EmployeeStatusSelect } from "@/components/forms/EmployeeStatusSelect";
import { composeAddressLine, defaultCountry, emptyAddressFields } from "@/lib/address";
import { zodResolver } from "@hookform/resolvers/zod";
import { Client, Employee, insertEmployeeSchema } from "@shared/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import { EligibilityBadge } from "@/components/ui/eligibility-badge";
import { DueDateCountdown } from "@/components/ui/due-date-countdown";
import {
  Download,
  FileText,
  FileUp,
  Users,
  X,
  Shield,
  ChevronDown,
  ChevronUp,
  ChevronsUpDown,
} from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import React, {
  ChangeEvent,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useForm } from "react-hook-form";
import { tabWithErrors, validatePersonalStep } from "@/lib/addEmployeeSteps";
import { Link } from "wouter";
import { EmployeeExportDialog } from "@/components/employees/EmployeeExportDialog";
import { EmployeeImport } from "@/components/employees/EmployeeImport";
import { formatStatus } from "@/lib/employeeStatus";
import { formatDateSafe, formatPhone, formatSSNInput } from "@/lib/utils";
import { formatPhoneInputValue } from "@/lib/phone";
import { useEmployeeDefaultSettings } from "@/hooks/useEmployeeDefaultSettings";
import {
  COMPENSATION_TYPE_OPTIONS,
  PAY_CYCLE_OPTIONS,
} from "@/lib/employeeDefaults";
import { OrganizationSelect } from "@/components/forms/OrganizationSelect";
import { sortEmployeesByFullName, buildNameSortKey } from "@/lib/employeeSort";

export default function ClientEmployees({
  employees,
  client,
  employeesLoading,
  clientIdParam,
}: {
  employees: Employee[];
  client: Client;
  employeesLoading: boolean;
  clientIdParam: string;
}) {
  const [employeeSearchTerm, setEmployeeSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("active");
  const [isEmployeeDialogOpen, setIsEmployeeDialogOpen] = useState(false);
  const [addEmployeeTab, setAddEmployeeTab] = useState("personal");
  const [showEligibility, setShowEligibility] = useState(false);
  const [selectedPlanId, setSelectedPlanId] = useState<string>("");
  
  // Sorting state
  type SortField = 'name' | 'status' | 'department' | 'position' | 'location' | 'email' | 'phone';
  const [sortField, setSortField] = useState<SortField | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  const [nameSortBasis, setNameSortBasis] = useState<'firstName' | 'lastName'>('firstName');

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field: SortField) => {
    if (sortField !== field) {
      return <ChevronsUpDown className="h-4 w-4 ml-1 text-gray-400" />;
    }
    return sortDirection === 'asc' 
      ? <ChevronUp className="h-4 w-4 ml-1" /> 
      : <ChevronDown className="h-4 w-4 ml-1" />;
  };

  // Fetch bulk eligibility data for all employees
  const { data: eligibilityData } = useQuery({
    queryKey: ["client-employees-eligibility", client.id],
    queryFn: async () => {
      const response = await fetch(
        `/api/clients/${client.id}/employees/eligibility`
      );
      if (!response.ok) return null;
      return response.json();
    },
    enabled: !!client.id && employees.length > 0,
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
  });

  // Fetch client plans for the eligibility plan selector
  const { data: clientPlans = [] } = useQuery<any[]>({
    queryKey: ["/api/client-plans", client.id],
    queryFn: async () => {
      const response = await fetch(`/api/client-plans?clientId=${client.id}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!client.id,
  });

  // Fetch company locations for mapping locationId to name
  const { data: companyLocations = [] } = useQuery<any[]>({
    queryKey: ["/api/org/locations", "client", client.id],
    queryFn: async () => {
      const response = await fetch(`/api/org/locations?ownerType=client&ownerId=${client.id}`);
      if (!response.ok) return [];
      return response.json();
    },
    enabled: !!client.id,
  });

  // Create a map of location ID to location name
  const locationById = useMemo(() => {
    const map = new Map<number, string>();
    for (const loc of companyLocations) {
      map.set(loc.id, loc.officeName || loc.name || `${loc.city}, ${loc.state}`);
    }
    return map;
  }, [companyLocations]);

  // Create a map of employee ID to eligibility info for quick lookup
  const eligibilityByEmployee = useMemo(() => {
    const map = new Map<number, any>();
    if (eligibilityData?.employees) {
      for (const emp of eligibilityData.employees) {
        map.set(emp.employeeId, emp);
      }
    }
    return map;
  }, [eligibilityData]);

  // Auto-select first plan when eligibility is enabled
  useEffect(() => {
    if (showEligibility && !selectedPlanId && clientPlans.length > 0) {
      const firstPlan = clientPlans[0]?.plan || clientPlans[0];
      if (firstPlan?.id) {
        setSelectedPlanId(String(firstPlan.id));
      }
    }
  }, [showEligibility, selectedPlanId, clientPlans]);

  // Helper to get eligibility for the selected plan
  const getEmployeeEligibility = (employeeId: number) => {
    const empEligibility = eligibilityByEmployee.get(employeeId);
    if (!empEligibility || !selectedPlanId) return null;

    // Find eligibility for selected plan
    return empEligibility.plans?.find(
      (p: any) => String(p.planId) === selectedPlanId
    ) || null;
  };

  const handlePhoneFieldChange = (
    event: ChangeEvent<HTMLInputElement>,
    onChange: (value: string) => void
  ) => {
    const formattedValue = formatPhoneInputValue(event.target.value);
    onChange(formattedValue);
  };

  const formatMoneyInput = (raw: string) => {
    const cleaned = raw.replace(/[^0-9.]/g, "");
    const parts = cleaned.split(".");
    if (parts.length === 1) return parts[0];
    const whole = parts[0];
    const frac = parts[1].slice(0, 2);
    return `${whole}.${frac}`;
  };

  const employeeForm = useForm({
    resolver: zodResolver(insertEmployeeSchema),
    defaultValues: {
      clientId: client.id,
      firstName: "",
      lastName: "",
      email: "",
      workPhone: "",
      personalEmail: "",
      phone: "",
      dateOfBirth: "",
      sex: "",
      ssn: "",
      hireDate: "",
      department: "",
      position: "",
      employmentType: "",
      salary: "",
      status: "",
      payCycle: "",
      compensationType: "",
      departmentId: null,
      jobTitleId: null,
      locationId: null,
      employeeClassId: null,
      scheduledHours: "",
      ...emptyAddressFields(),
    },
  });

  const { defaults: employeeDefaults } = useEmployeeDefaultSettings(
    "client",
    String(client.id)
  );

  const preloadEmployeeDefaults = useCallback(() => {
    const currentValues = employeeForm.getValues();
    if (employeeForm.formState.isDirty) {
      return;
    }

    const nextValues = { ...currentValues };
    let valuesChanged = false;

    const applyIfEmpty = (
      fieldName: keyof typeof nextValues,
      candidate?: string
    ) => {
      if (!candidate || candidate.trim().length === 0) {
        return;
      }
      const current = nextValues[fieldName];
      if (typeof current === "string" && current.trim().length > 0) {
        return;
      }
      nextValues[fieldName] = candidate;
      valuesChanged = true;
    };

    applyIfEmpty("employmentType", employeeDefaults.employmentType);
    applyIfEmpty("status", employeeDefaults.status || "active");
    applyIfEmpty("payCycle", employeeDefaults.payCycle);
    applyIfEmpty("compensationType", employeeDefaults.compensationType);
    applyIfEmpty("city", employeeDefaults.city);
    applyIfEmpty("state", employeeDefaults.state);
    applyIfEmpty("county", employeeDefaults.county);

    const desiredCountry = employeeDefaults.country?.trim();
    if (desiredCountry && desiredCountry.length > 0) {
      const currentCountry = (nextValues.country || "").trim();
      if (
        currentCountry.length === 0 ||
        currentCountry.toLowerCase() === defaultCountry.toLowerCase()
      ) {
        nextValues.country = desiredCountry;
        valuesChanged = true;
      }
    }

    if (!valuesChanged) {
      return;
    }

    employeeForm.reset(nextValues);
  }, [employeeDefaults, employeeForm]);

  useEffect(() => {
    if (!isEmployeeDialogOpen) {
      return;
    }
    preloadEmployeeDefaults();
  }, [isEmployeeDialogOpen, preloadEmployeeDefaults]);

  const handleCreateEmployee = (values: any) => {
    const employeePayload = {
      ...values,
      address: composeAddressLine(values),
    };

    createEmployeeMutation.mutate(employeePayload);
  };

  const createEmployeeMutation = useMutation({
    mutationFn: async (employee: any) => {
      const response = await apiRequest("POST", `/api/employees`, employee);
      const result = await response.json();
      return result;
    },
    onSuccess: (result: any) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/clients", clientIdParam, "employees"],
      });
      employeeForm.reset();
      setIsEmployeeDialogOpen(false);
      toast({
        title: "Employee added",
        description:
          "Account created — send login from the Logins tab when ready.",
        variant: "success",
      });
    },
    onError: (error) => {
      console.error("Employee creation error:", error);
      toast({ title: "Failed to add employee", variant: "destructive" });
    },
  });

  // Delete action is disabled in this view


  const sortedEmployees = useMemo(() => {
    return sortEmployeesByFullName(employees);
  }, [employees]);

  const filteredEmployees = useMemo(() => {
    let filtered = sortedEmployees;

    // Filter by status
    if (statusFilter !== "all") {
      filtered = filtered.filter(
        (emp) => normalizeEmploymentValue(emp.status) === statusFilter
      );
    }

    // Filter by search term
    const trimmedSearchTerm = employeeSearchTerm.trim();
    if (trimmedSearchTerm.length > 0) {
      const normalizedSearch = trimmedSearchTerm.toLowerCase();
      filtered = filtered.filter((employee: any) => {
        return (
          employee.firstName?.toLowerCase().includes(normalizedSearch) ||
          employee.lastName?.toLowerCase().includes(normalizedSearch) ||
          employee.email?.toLowerCase().includes(normalizedSearch) ||
          employee.department?.toLowerCase().includes(normalizedSearch) ||
          employee.position?.toLowerCase().includes(normalizedSearch)
        );
      });
    }

    // Apply sorting if a sort field is selected
    if (!sortField) return filtered;

    return [...filtered].sort((a: any, b: any) => {
      let aValue: string;
      let bValue: string;

      switch (sortField) {
        case 'name':
          aValue = buildNameSortKey(a, nameSortBasis);
          bValue = buildNameSortKey(b, nameSortBasis);
          break;
        case 'status':
          aValue = (a.status || '').toLowerCase();
          bValue = (b.status || '').toLowerCase();
          break;
        case 'department':
          aValue = (a.department || '').toLowerCase();
          bValue = (b.department || '').toLowerCase();
          break;
        case 'position':
          aValue = (a.position || '').toLowerCase();
          bValue = (b.position || '').toLowerCase();
          break;
        case 'location':
          aValue = (a.city || '').toLowerCase();
          bValue = (b.city || '').toLowerCase();
          break;
        case 'email':
          aValue = (a.email || '').toLowerCase();
          bValue = (b.email || '').toLowerCase();
          break;
        case 'phone':
          aValue = (a.phone || '').toLowerCase();
          bValue = (b.phone || '').toLowerCase();
          break;
        default:
          return 0;
      }

      if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [sortedEmployees, employeeSearchTerm, statusFilter, sortField, sortDirection, nameSortBasis]);

  return (
    <>
      {" "}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-medium">Employees</h3>
        <div className="flex items-center space-x-2">
{/* Eligibility Toggle - Hidden per user request */}
          <Select
            value={nameSortBasis}
            onValueChange={(v) => {
              // also activates the sort, not just the basis for the next header click
              setNameSortBasis(v as 'firstName' | 'lastName');
              setSortField('name');
              setSortDirection('asc');
            }}
          >
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Sort name by" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="firstName">First Name</SelectItem>
              <SelectItem value="lastName">Last Name</SelectItem>
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {EMPLOYEE_STATUSES.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <div className="relative">
            <Input
              placeholder="Search employees..."
              value={employeeSearchTerm}
              onChange={(e) => setEmployeeSearchTerm(e.target.value)}
              className="w-64"
            />
          </div>
          <EmployeeExportDialog
            employees={filteredEmployees}
            filenameBase={`${client?.name || "client"}_employees`}
          />
          <EmployeeImport
            clientId={client.id}
            onImportComplete={() => queryClient.invalidateQueries({ queryKey: ["/api/clients", clientIdParam, "employees"] })}
            renderTrigger={(onClick) => (
              <PermissionButton permission="clients:edit" whenDenied="hide" variant="outline" onClick={onClick}>
                <FileUp className="h-4 w-4 mr-2" />
                Import
              </PermissionButton>
            )}
          />
          <Dialog
            open={isEmployeeDialogOpen}
            onOpenChange={setIsEmployeeDialogOpen}
          >
            <DialogTrigger asChild>
              <PermissionButton permission="clients:edit" whenDenied="hide">
                <Users className="h-4 w-4 mr-2" />
                Add Employee
              </PermissionButton>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Add New Employee</DialogTitle>
              </DialogHeader>
              <Form {...employeeForm}>
                <form
                  onSubmit={employeeForm.handleSubmit(handleCreateEmployee, (errors) =>
                    setAddEmployeeTab(tabWithErrors(errors))
                  )}
                  className="space-y-4"
                >
                  <Tabs value={addEmployeeTab} onValueChange={setAddEmployeeTab} className="w-full">
                    <TabsList className="grid w-full grid-cols-2">
                      <TabsTrigger value="personal">Personal / Profile</TabsTrigger>
                      <TabsTrigger value="employment">Employment</TabsTrigger>
                    </TabsList>

                    {/* Personal Tab */}
                    <TabsContent value="personal" className="space-y-4 mt-4">
                      {/* Row 1: Name */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="firstName"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>First Name <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <Input placeholder="John" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="lastName"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Last Name <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <Input placeholder="Doe" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 2: Personal Email + Phone */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="personalEmail"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-8">
                              <FormLabel>Personal Email</FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john.doe@gmail.com"
                                  {...field}
                                  value={field.value || ""}
                                  onBlur={(e) =>
                                    field.onChange(e.target.value.trim().toLowerCase())
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="phone"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Phone</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value || ""}
                                  placeholder="(555) 123-4567"
                                  onChange={(e) => handlePhoneFieldChange(e, field.onChange)}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 3: Date of Birth + Sex + SSN */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="dateOfBirth"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Date of Birth</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} value={field.value || ""} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="sex"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Sex</FormLabel>
                              <Select
                                value={field.value || ""}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select sex" />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="male">Male</SelectItem>
                                  <SelectItem value="female">Female</SelectItem>
                                  <SelectItem value="other">Other</SelectItem>
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="ssn"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>SSN</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value || ""}
                                  onChange={(e) => field.onChange(formatSSNInput(e.target.value))}
                                  inputMode="numeric"
                                  maxLength={11}
                                  placeholder="123-45-6789"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Address */}
                      <AddressFields control={employeeForm.control} />

                      {/* Cancel + Next Buttons */}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsEmployeeDialogOpen(false);
                            setAddEmployeeTab("personal");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="button"
                          onClick={async () => {
                            if (await validatePersonalStep(employeeForm)) setAddEmployeeTab("employment");
                          }}
                        >
                          Next
                        </Button>
                      </div>
                    </TabsContent>

                    {/* Employment Tab */}
                    <TabsContent value="employment" className="space-y-4 mt-4">
                      {/* Row 1: Work Email + Work Phone */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="email"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-8">
                              <FormLabel>Work Email <span className="text-red-500">*</span></FormLabel>
                              <FormControl>
                                <Input
                                  type="email"
                                  placeholder="john.doe@company.com"
                                  {...field}
                                  onBlur={(e) =>
                                    field.onChange(e.target.value.trim().toLowerCase())
                                  }
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="workPhone"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-4">
                              <FormLabel>Work Phone</FormLabel>
                              <FormControl>
                                <Input
                                  {...field}
                                  value={field.value || ""}
                                  placeholder="(555) 123-4567"
                                  onChange={(e) => {
                                    const formatted = formatPhoneInputValue(e.target.value);
                                    field.onChange(formatted);
                                  }}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 2: Department + Job Title */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="departmentId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Department</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="department"
                                  ownerType="client"
                                  ownerId={client.id}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Department"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="jobTitleId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Job Title</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="job-title"
                                  ownerType="client"
                                  ownerId={client.id}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Job Title"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 3: Location + Employee Class */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="locationId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Location</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="location"
                                  ownerType="client"
                                  ownerId={client.id}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Location"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="employeeClassId"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Employee Class</FormLabel>
                              <FormControl>
                                <OrganizationSelect
                                  orgType="employee-class"
                                  ownerType="client"
                                  ownerId={client.id}
                                  value={field.value}
                                  onChange={field.onChange}
                                  placeholder="Select Employee Class"
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 4: Hire Date + Salary */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="hireDate"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Hire Date</FormLabel>
                              <FormControl>
                                <Input type="date" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="salary"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Salary</FormLabel>
                              <FormControl>
                                <Input placeholder="$65,000" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 5: Scheduled Hours */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="scheduledHours"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Hours Worked (Weekly)</FormLabel>
                              <FormControl>
                                <Input placeholder="40" {...field} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 6: Compensation Type + Pay Cycle */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="compensationType"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Compensation Type</FormLabel>
                              <Select
                                value={field.value || ""}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select compensation type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {COMPENSATION_TYPE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="payCycle"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Pay Cycle</FormLabel>
                              <Select
                                value={field.value || ""}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select pay cycle" />
                                </SelectTrigger>
                                <SelectContent>
                                  {PAY_CYCLE_OPTIONS.map((option) => (
                                    <SelectItem key={option.value} value={option.value}>
                                      {option.label}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Row 7: Employment Type + Status */}
                      <div className="grid grid-cols-12 gap-4">
                        <FormField
                          control={employeeForm.control}
                          name="employmentType"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Employment Type</FormLabel>
                              <Select
                                value={field.value}
                                onValueChange={field.onChange}
                              >
                                <SelectTrigger>
                                  <SelectValue placeholder="Select employment type" />
                                </SelectTrigger>
                                <SelectContent>
                                  {EMPLOYMENT_TYPES.map((t) => (
                                    <SelectItem key={t} value={t}>
                                      {t}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                        <FormField
                          control={employeeForm.control}
                          name="status"
                          render={({ field }) => (
                            <FormItem className="col-span-12 sm:col-span-6">
                              <FormLabel>Status <span className="text-red-500">*</span></FormLabel>
                              <EmployeeStatusSelect
                                value={field.value}
                                onChange={field.onChange}
                                placeholder="Select status"
                              />
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      </div>

                      {/* Cancel + Add Employee Buttons */}
                      <div className="flex justify-end gap-2 pt-2">
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => {
                            setIsEmployeeDialogOpen(false);
                            setAddEmployeeTab("personal");
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          type="submit"
                          disabled={createEmployeeMutation.isPending}
                        >
                          {createEmployeeMutation.isPending ? "Adding..." : "Add Employee"}
                        </Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                </form>
              </Form>
            </DialogContent>
          </Dialog>
        </div>
      </div>
      {employeesLoading ? (
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      ) : sortedEmployees.length > 0 ? (
        <div className="bg-white dark:bg-gray-900 shadow overflow-hidden sm:rounded-md">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800">
                <tr>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center">
                      Employee Name
                      {getSortIcon('name')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Hire Date
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                  >
                    Type
                  </th>
                  {showEligibility && (
                    <>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Eligibility
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        Effective Date
                      </th>
                      <th
                        scope="col"
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        ACA
                      </th>
                    </>
                  )}
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('department')}
                  >
                    <div className="flex items-center">
                      Department
                      {getSortIcon('department')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('position')}
                  >
                    <div className="flex items-center">
                      Title
                      {getSortIcon('position')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('location')}
                  >
                    <div className="flex items-center">
                      Location
                      {getSortIcon('location')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('email')}
                  >
                    <div className="flex items-center">
                      Email
                      {getSortIcon('email')}
                    </div>
                  </th>
                  <th
                    scope="col"
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 select-none"
                    onClick={() => handleSort('phone')}
                  >
                    <div className="flex items-center">
                      Phone
                      {getSortIcon('phone')}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                {filteredEmployees.map((employee) => (
                  <tr key={employee.id} className="hover:bg-gray-50 dark:hover:bg-gray-800">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10">
                          <div className="h-10 w-10 rounded-full bg-gray-300 dark:bg-gray-600 flex items-center justify-center">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-200">
                              {employee.firstName?.[0] || ""}
                              {employee.lastName?.[0] || ""}
                            </span>
                          </div>
                        </div>
                        <div className="ml-4">
                          <Link
                            href={`/clients/${clientIdParam}/employees/${employee.id}`}
                          >
                            <div className="text-sm font-medium text-gray-900 dark:text-gray-100 hover:text-blue-600 dark:hover:text-blue-400 cursor-pointer">
                              {employee.firstName} {employee.lastName}
                            </div>
                          </Link>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          employee.status?.toLowerCase() === "active"
                            ? "bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300"
                            : employee.status?.toLowerCase() === "inactive"
                            ? "bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300"
                            : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300"
                        }`}
                      >
                        {formatStatus(employee.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      <div className="flex items-center gap-2">
                        {employee.hireDate
                          ? formatDateSafe(employee.hireDate as any)
                          : "N/A"}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {(employee as any).employmentType || (
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-300">
                          Missing
                        </span>
                      )}
                    </td>
                    {showEligibility && (
                      <>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(() => {
                            const planEligibility = getEmployeeEligibility(employee.id);
                            if (!planEligibility)
                              return <span className="text-gray-400 dark:text-gray-500">-</span>;
                            const status = !planEligibility.isEligible
                              ? "ineligible"
                              : planEligibility.daysUntilEffective > 0
                              ? "pending"
                              : "eligible";
                            const label = !planEligibility.isEligible
                              ? "Ineligible"
                              : planEligibility.daysUntilEffective > 0
                              ? `In ${planEligibility.daysUntilEffective}d`
                              : "Eligible";
                            return (
                              <EligibilityBadge
                                status={status as any}
                                label={label}
                                tooltip={planEligibility.ineligibilityReason}
                                showIcon={true}
                              />
                            );
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                          {(() => {
                            const planEligibility = getEmployeeEligibility(employee.id);
                            if (!planEligibility?.isEligible) return "-";
                            return planEligibility.effectiveDate || "-";
                          })()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          {(() => {
                            const planEligibility = getEmployeeEligibility(employee.id);
                            if (!planEligibility || !planEligibility.isEligible) return "-";

                            // Non-compliant (exceeds 90 days)
                            if (!planEligibility.acaCompliant) {
                              return (
                                <span
                                  className="text-red-500 cursor-help"
                                  title={planEligibility.acaWarning || `${planEligibility.totalWaitingDays} days exceeds ACA 90-day limit`}
                                >
                                  ❌ {planEligibility.totalWaitingDays}
                                </span>
                              );
                            }

                            // Warning (approaching limit, within 15 days)
                            if (planEligibility.acaWarning) {
                              return (
                                <span
                                  className="text-amber-500 cursor-help"
                                  title={planEligibility.acaWarning}
                                >
                                  ⚠️ {planEligibility.totalWaitingDays}
                                </span>
                              );
                            }

                            // Compliant
                            return (
                              <span
                                className="text-green-500 cursor-help"
                                title={`${planEligibility.totalWaitingDays} days total waiting period (ACA limit: 90 days)`}
                              >
                                ✓ {planEligibility.totalWaitingDays}
                              </span>
                            );
                          })()}
                        </td>
                      </>
                    )}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.department || "Not specified"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.position || "Not specified"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {(employee as any).locationId ? locationById.get((employee as any).locationId) || "-" : "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.email}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 dark:text-gray-100">
                      {employee.phone ? formatPhone(employee.phone) : "N/A"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white dark:bg-card shadow sm:rounded-md p-6 text-center border dark:border-border">
          <Users className="mx-auto h-12 w-12 text-gray-400 dark:text-gray-500" />
          <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">
            No employees
          </h3>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            Get started by adding your first employee.
          </p>
          <div className="mt-6">
            <PermissionButton
              permission="clients:edit"
              whenDenied="hide"
              onClick={() => setIsEmployeeDialogOpen(true)}
            >
              <Users className="h-4 w-4 mr-2" />
              Add Employee
            </PermissionButton>
          </div>
        </div>
      )}
    </>
  );
}
